import { Component, OnInit } from '@angular/core';
import { LayoutService } from 'libs/portal/src/layout/layout.service';
import { SegmentedTileModel } from 'libs/portal/src/modules/keepalive/models/SegmentedTile';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { UserRightsService } from 'libs/portal/src/services/user-rights/user-rights.service';
import { Endpoints } from '@huge-monorepo/portal/config';

@Component({
  selector: 'app-mikrotik-dashboard',
  templateUrl: './mikrotik-dashboard.page.html',
  styleUrls: ['./mikrotik-dashboard.page.scss'],
})
export class MikrotikDashboardPageComponent implements OnInit {
  constructor(
    public layoutService: LayoutService,
    private genericService: GenericService,
    private endpoints: Endpoints,
    private auth: UserRightsService,
    private tools: ToolsService,
  ) {}

  groupId: number;
  activeDeviceHistory: SegmentedTileModel[] = [];
  mikrotikHealth: any;
  mikrotikStatusesRow1: any[] = [];
  mikrotikStatusesRow2: any[] = [];
  disableCaching = false;
  devicesOverTimeData: any[];

  monthlyToggle = false;
  dailyToggle = false;
  daterangeStart: any = null;
  daterangeEnd: any = null;
  currTimeFrame = 3;

  ngOnInit() {
    this.disableCaching = this.auth.HasSessionChanged();
    if (this.disableCaching) {
      this.auth.SetSessionChanged(false);
    }
    setTimeout(() => {
      this.groupId = this.auth.GetGroupID();
      this.getDashboardData();
    });
  }

  getDashboardData() {
    this.getActiveDeviceHistory();
    this.getDevicesHealth();
    this.getDeviceStatusOvertime();
  }

  getActiveDeviceHistory() {
    const url = this.endpoints.GET_DEVICE_ONLINE_RANGES + (this.disableCaching ? '?opn=true' : '');
    this.genericService
      .POST(url, {
        GroupID: this.groupId,
      })
      .subscribe((results: any) => {
        const data = results['body'];
        if (data != null) {
          this.activeDeviceHistory.splice(0, 0, {
            title: 'MikroTik',
            values: [
              {
                value: data.MikrotikPing.Online + data.MikrotikPing.LessTwelveHours,
                label: 'Online',
                valueColor: 'primary',
                labelColor: 'primary',
              },
              {
                value: data.MikrotikPing.GreaterTwelveHours,
                label: '> 12 hours ago',
                valueColor: 'primary',
                labelColor: 'primary',
              },
              {
                value: data.MikrotikPing.Day,
                label: '> 1 day ago',
                valueColor: 'primary',
                labelColor: 'primary',
              },
              {
                value: data.MikrotikPing.Week,
                label: '> 1 week ago',
                valueColor: 'primary',
                labelColor: 'primary',
              },
            ],
          });
        }
      });
  }

  getDevicesHealth() {
    this.genericService
      .POST(this.endpoints.GET_DEVICES_HEALTH, {
        GroupID: this.groupId,
        Mikrotik: true,
        KeepAlive: false,
        CDR: false,
        Start: this.tools.FormatStartDate(this.tools.GetStartOfMonth(new Date()).toISOString()),
        End: this.tools.FormatEndDate(this.tools.GetEndOfMonth().toISOString()),
      })
      .subscribe((results: any) => {
        const data = results['body'];
        if (data != null) {
          this.mikrotikHealth = data.Mikrotik;
          this.mikrotikHealth.Health = Math.round(this.mikrotikHealth.Health);
          this.mikrotikStatusesRow1 = [
            {
              title: 'Total',
              titleColor: 'primary',
              value: `${data.Mikrotik.Total}`,
              valueColor: 'primary',
              tileColor: 'light',
            },
            {
              title: 'Online (<30% Packet Loss)',
              titleColor: 'light',
              value: `${data.Mikrotik.NowOnline}`,
              valueColor: 'light',
              tileColor: 'success',
            },
          ];

          this.mikrotikStatusesRow2 = [
            {
              title: 'Flapping (30% - 70% Packet Loss)',
              titleColor: 'light',
              value: `${data.Mikrotik.NowFlapping}`,
              valueColor: 'light',
              tileColor: 'warning',
            },
            {
              title: 'Offline (>70% Packet Loss)',
              titleColor: 'light',
              value: `${data.Mikrotik.NowOffline}`,
              valueColor: 'light',
              tileColor: 'danger',
            },
          ];
        }
      });
  }

  getDeviceStatusOvertime(timeframe?, start?, end?, period?) {
    const reqObj = {
      Start: this.tools.FormatStartDate(this.tools.GetStartOfMonth(new Date()).toISOString()),
      End: this.tools.FormatEndDate(this.tools.GetEndOfMonth().toISOString()),
      Interval: 1,
      AllDevices: true,
      CDR: false,
      Mikrotik: true,
      KeepAlive: false,
    };

    if (start != null && end != null) {
      reqObj.Start = start;
      reqObj.End = end;
    }

    if (period != null) {
      reqObj['TimePeriod'] = period;
    }

    if (timeframe != null) {
      reqObj['TimeFrame'] = timeframe;
    }

    const url = this.endpoints.GET_DEVICE_STATUS_OVER_TIME + (this.disableCaching ? '?opn=true' : '');

    this.genericService.POST(url, reqObj).subscribe((results: any) => {
      const data = results['body'];
      if (data.MikrotikOverTime != null) {
        this.devicesOverTimeData = data.MikrotikOverTime;
      } else {
        this.devicesOverTimeData = [];
      }
    });
  }

  monthlyToggleChanged() {
    setTimeout(() => {
      this.monthlyToggle = !this.monthlyToggle;
      if (!this.monthlyToggle) {
        this.devicesOverTimeData = null;
        if (this.dailyToggle) {
          this.dailyToggleChanged(false);
        } else {
          this.getDeviceStatusOvertime();
        }
      } else {
        this.devicesOverTimeData = null;
        this.currTimeFrame = 3;
        this.getDeviceStatusOvertime(this.currTimeFrame, null, null, 'Month');
      }
    });
  }

  dailyToggleChanged(val = true) {
    // Optional value just to skip changing of daily toggle
    setTimeout(() => {
      if (val) {
        this.dailyToggle = !this.dailyToggle;
      }

      if (!this.dailyToggle) {
        this.devicesOverTimeData = null;
        this.getDeviceStatusOvertime();
      } else {
        const endDate = this.tools.FormatEndDate(new Date().toString());
        const startDate = this.tools.FormatStartDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toString());
        this.devicesOverTimeData = null;
        this.getDeviceStatusOvertime(null, startDate, endDate);
      }
    });
  }

  timeFrameChange(timeframe) {
    this.currTimeFrame = parseInt(timeframe);

    this.devicesOverTimeData = null;
    this.getDeviceStatusOvertime(this.currTimeFrame, null, null, 'Month');
  }

  startDateInput(startDate) {
    this.daterangeStart = startDate;
  }

  endDateInput(endDate) {
    this.daterangeEnd = endDate;

    this.devicesOverTimeData = null;
    // this.getDeviceStatusOvertime(start, end, "Month");
  }
}
